import { useRef, useState } from 'react';
import type { MutableRefObject, PropsWithChildren, Ref } from 'react';
import { Flex } from '@chakra-ui/react';
import { useScrollListener } from '../../hooks/useScrollListener';
import { useGetContentSlot } from '../../utils/api';
import { ContentSlotID } from '../../utils/api/ContentSlotID';
import { classNames } from '../../utils/tailwind/classNames';
import { sanitizeHtml } from '../content-asset/sanitize-html';
import { CloseIcon } from '../icons';
import styles from './Header.module.scss';
import { Menu } from './menu';

export const MAIN_NAV_HEADER_ID = 'slt-page-nav-header';

interface Props extends PropsWithChildren {
    headerRef?: Ref<HTMLElement>;
    stickyNavigation?: boolean;
}

export const Header: React.FC<Props> = ({ headerRef, stickyNavigation = true }: Props) => {
    const slotTopHeader = useGetContentSlot(ContentSlotID.TOP_HEADER);
    const slotShippingBanner = useGetContentSlot(ContentSlotID.SHIPPING_BANNER);
    const [showSlotTopHeader, setShowSlotTopHeader] = useState(true);
    const { isNotScrolled } = useScrollListener();
    const newRef = useRef<HTMLElement | null>(null);
    const ref = headerRef ?? newRef;

    return (
        <header
            id={MAIN_NAV_HEADER_ID}
            ref={ref}
            className={classNames(
                'top-0 isolate z-[150] flex w-full flex-col items-center bg-white',
                stickyNavigation ? 'sticky' : 'relative',
            )}
        >
            {showSlotTopHeader && slotTopHeader.data ? (
                <Flex
                    data-testid="header.top-header-banner"
                    backgroundColor="#eee"
                    alignItems="flex-start"
                    width="100%"
                    order={5}
                    className="py-2 xs:px-4 md:px-6"
                >
                    <Flex
                        data-testid="header.top-header-banner-text"
                        textStyle="body-small"
                        lineHeight="130%"
                        color="slt-black.900"
                        className={`!font-bold ${styles.top_header}`}
                        justifyContent="center"
                        textAlign="center"
                        flexGrow={1}
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(slotTopHeader.data),
                        }}
                    />

                    <CloseIcon
                        data-testid="header.top-header-banner-icon"
                        cursor="pointer"
                        onClick={(): void => setShowSlotTopHeader(false)}
                    />
                </Flex>
            ) : null}

            <Menu headerRef={ref as MutableRefObject<HTMLElement>} />

            {slotShippingBanner.data && (!stickyNavigation || isNotScrolled) ? (
                <Flex
                    data-testid="header.shipping-banner"
                    className={styles.shipping_banner}
                    order={{ base: 1, md: 15 }}
                    textStyle="body-small"
                    fontWeight={400}
                    direction="column"
                    width="100%"
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(slotShippingBanner.data),
                    }}
                />
            ) : null}
        </header>
    );
};

Header.defaultProps = {
    headerRef: undefined,
    stickyNavigation: true,
};
